import type { ReactNode } from 'react';
import { Box, Flex, Icon, PlainButton, Tooltip } from '@customerio/pluma-components/react';
import {
  ConnectionStatus,
  DataFlow,
  IntegrationType,
  PipelinesIntegrationType,
} from '@/features/integrations/catalog/enums';
import { IntegrationIDs } from '@/features/integrations/catalog/ids';
import { getPipelinesIntegrationImageSrcOrIcon } from '@/features/integrations/catalog/images';
import type { IntegrationConnection } from '@/features/integrations/connections/view-model/types';
import { ProductApp } from '@/features/integrations/connections/view-model/types';
import { ConnectedItem, ConnectionIcon } from './connection-icon';
import { ConnectionGroup } from './connection-group';
import { FlowIndicator } from './flow-indicator';
import * as styles from './styles.css';

const DEFAULT_MAX_DISPLAY_COUNT = 3;

function resolveMaxDisplayCount(maxDisplay: number | undefined): number {
  if (maxDisplay == null || maxDisplay <= 0 || Number.isNaN(maxDisplay)) {
    return DEFAULT_MAX_DISPLAY_COUNT;
  }
  return maxDisplay;
}

function getFlowTypeLabel(connection: Pick<IntegrationConnection, 'dataFlow'>): string {
  return connection.dataFlow === DataFlow.in ? 'Data-in' : 'Data-out';
}

function getOppositeConnectionType(direction: string): string | null {
  if (direction === IntegrationType.pipelinesSource) {
    return PipelinesIntegrationType.pipelinesDestination;
  }
  if (direction === IntegrationType.pipelinesDestination) {
    return PipelinesIntegrationType.pipelineSource;
  }
  return null;
}

function resolveRelatedConnections(
  connection: IntegrationConnection,
  resolveRelated: (lookupId: string) => IntegrationConnection | null,
): IntegrationConnection[] {
  if (connection.connectedTo == null || connection.connectedTo.length === 0 || connection.direction == null) {
    return [];
  }

  const oppositeType = getOppositeConnectionType(connection.direction);
  if (oppositeType == null) {
    return [];
  }

  const related: IntegrationConnection[] = [];
  for (const connectedId of connection.connectedTo) {
    if (connectedId == null) {
      continue;
    }
    const lookupId = `pipelines_${oppositeType}_${connectedId}`;
    const resolved = resolveRelated(lookupId);
    if (resolved !== null) {
      related.push(resolved);
    }
  }
  return related;
}

type ThisWorkspaceIconProps = {
  className?: string;
};

function ThisWorkspaceIcon({ className }: ThisWorkspaceIconProps) {
  const { imageSrc } = getPipelinesIntegrationImageSrcOrIcon('customerio');
  return (
    <Box className={className}>
      <ConnectionIcon connection={{ imageSrc, status: ConnectionStatus.connected }} />
    </Box>
  );
}

type GroupConnectionsProps = {
  connection: IntegrationConnection;
  maxDisplay?: number;
  onClick?: (connectionId: string) => void;
  /**
   * Resolves a related Pipelines connection by its lookup id
   * (`pipelines_PipelinesSource_<id>` / `pipelines_PipelinesDestination_<id>`, which
   * is the id format the backend hands back — `ui_api` builds it from the gaia
   * `ConnectionType` enum name). Ember resolves these with `store.peekRecord`; the
   * table supplies the equivalent index. Without a resolver every Pipelines row
   * falls through to the bare "add a connection" button.
   */
  resolveRelated?: (lookupId: string) => IntegrationConnection | null;
};

export function GroupConnections({
  connection,
  maxDisplay,
  onClick,
  resolveRelated = () => null,
}: GroupConnectionsProps) {
  const maxDisplayCount = resolveMaxDisplayCount(maxDisplay);
  const isPrimaryConnectionSource = connection.dataFlow === DataFlow.in;
  const showPrimaryConnectionSource = isPrimaryConnectionSource && connection.id !== IntegrationIDs.customerioAPI;
  const isDataOut = !isPrimaryConnectionSource;
  const isDefined = connection.connectedTo != null;
  const hasSupportToAddConnections = connection.product === ProductApp.dataPipelines && connection.connectedTo != null;
  const emptyStateTooltip = isPrimaryConnectionSource
    ? 'Data flows to your workspace'
    : 'Data flows from your workspace';

  const relatedConnections = resolveRelatedConnections(connection, resolveRelated);
  const hasRelatedConnections = relatedConnections.length > 0;

  const handleAddOnClick = (): void => {
    onClick?.(connection.id);
  };

  const renderContents = (): ReactNode => {
    if (hasRelatedConnections) {
      if (isPrimaryConnectionSource) {
        // Source -> Destination flow
        return (
          <>
            <ConnectedItem
              connection={connection}
              flowTypeLabel={getFlowTypeLabel(connection)}
              data-test-main-connection-icon
            />
            <FlowIndicator />
            <ConnectionGroup
              connections={relatedConnections}
              maxDisplay={maxDisplayCount}
              getFlowTypeLabel={getFlowTypeLabel}
              containerClass={styles.firstIconWrapper}
            />
          </>
        );
      }
      // Destination <- Source flow
      return (
        <>
          <ConnectionGroup
            connections={relatedConnections}
            maxDisplay={maxDisplayCount}
            getFlowTypeLabel={getFlowTypeLabel}
            containerClass={styles.destinationIconWrapper}
          />
          <FlowIndicator />
          <Flex className={styles.firstIconWrapper}>
            <ConnectedItem
              connection={connection}
              flowTypeLabel={getFlowTypeLabel(connection)}
              data-test-main-connection-icon
            />
          </Flex>
        </>
      );
    }

    if (hasSupportToAddConnections) {
      return (
        <PlainButton className={styles.moreIndicator} onClick={handleAddOnClick} data-test-add-connection>
          <Icon name="add" size="xs" color="subtle" />
        </PlainButton>
      );
    }

    if (isDefined) {
      return (
        <>
          {showPrimaryConnectionSource ? (
            <>
              <ConnectedItem connection={connection} flowTypeLabel="Data in" data-test-main-connection-icon />
              <FlowIndicator />
            </>
          ) : null}
          <Tooltip placement="top" content={emptyStateTooltip}>
            <ThisWorkspaceIcon className={showPrimaryConnectionSource ? styles.firstIconWrapper : undefined} />
          </Tooltip>
          {isDataOut ? (
            <>
              <FlowIndicator />
              <Flex className={styles.firstIconWrapper}>
                <ConnectedItem connection={connection} flowTypeLabel="Data out" data-test-main-connection-icon />
              </Flex>
            </>
          ) : null}
        </>
      );
    }

    return null;
  };

  return (
    <Flex alignItems="center" gap="050" className={styles.wrapper}>
      {renderContents()}
    </Flex>
  );
}
